import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Select,
  Row,
  Col,
  Tag,
  Switch,
  Space,
  DatePicker,
  Spin,
  Popconfirm,
  Alert,
  Progress,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  BarcodeOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductCategories,
  getProductUnits,
  getAllTaxes,
  getProductTags,
  bulkProductImageGenerate,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import ImageUpload from "../../../components/ImageUpload";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { AppContext } from "../../../context/AppContextProvider";
import "./Products.css";
import { useTranslation } from "react-i18next";
import {
  AddonName,
  ProductImageGenerationJobStatus,
  UserType,
} from "../../../configs/constants";
import DownloadImportTemplate from "../../../components/DownloadImportTemplate";
import ImportItems from "../../../components/ImportItems";
import moment from "moment";
import { arabicNameRules, cleanArabicInput } from "../../../utils/validators";
import { applyAttachmentToPayload } from "../../../utils/attachmentPayload";
import AddCategory from "../ProductCategories/AddCategory";
import AddUnit from "../ProductUnits/AddUnit";
import AddOption from "../ProductTags/AddOption";
import { getEcho } from "../../../bootstrap/echo";
import { setEchoAuthToken } from "../../../bootstrap/echoAuth";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const Products = () => {
  const ADD_PRODUCT = "Add Product";
  const { roundOff, appConfig, authUser } = useContext(AppContext);

  const [loadingBTN, setLoadingBTN] = useState(false);

  // Active background AI image generation job; results arrive over websockets.
  const [imageGenerationJob, setImageGenerationJob] = useState(null);

  const [isTaxable, setIsTaxable] = useState(true);
  const [isQRproduct, setIsQRproduct] = useState(true);
  const [isStockableProduct, setIsStockableProduct] = useState(true);
  const [isTicket, setIsTicket] = useState(true);
  const [isCalories, setIsCalories] = useState(false);

  const [unitsList, setUnitsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [productTagsList, setProductTagsList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [productsTableData, setProductsTableData] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [taxesData, setTaxesData] = useState([]);
  const [modalTitle, setModalTitle] = useState(ADD_PRODUCT);
  const [image, setImage] = useState(false);
  const [searchParams, setSearchParams] = useState([]);
  const [modalData, setModalData] = useState(null);

  const [productForm] = Form.useForm();
  const [searchFormRef] = Form.useForm();
  const { t } = useTranslation();
  const taxesSelect = useRef();

  const userBusinessOwner = authUser.type === UserType.BusinessOwner;
  const canManageProducts =
    userBusinessOwner || Boolean(authUser.can_add_edit_product);
  const activeAddons = authUser.active_addons || [];

  const reloadProducts = () => setReloadTableData((prev) => !prev);

  const is_QR_addon_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.QrOrdering,
  );

  const is_stock_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const is_ticketing_addon_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );

  const loadProductUnits = useCallback(() => {
    return getProductUnits()
      .then((response) => {
        const units = response?.data?.data?.product_units || [];

        if (response?.data?.success === true) {
          setUnitsList(units);
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }

        return units;
      })
      .catch((error) => {
        handleApiError(error, t);
        return [];
      });
  }, [t]);

  // ---------- Load units ----------
  useEffect(() => {
    loadProductUnits();
  }, [loadProductUnits]);

  const loadProductCategories = useCallback(() => {
    return getProductCategories()
      .then((response) => {
        const categories = response?.data?.data?.product_categories || [];

        if (response?.data?.success === true) {
          setCategoriesList(categories);
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }

        return categories;
      })
      .catch((error) => {
        handleApiError(error, t);
        return [];
      });
  }, [t]);

  // ---------- Load categories ----------
  useEffect(() => {
    loadProductCategories();
  }, [loadProductCategories]);

  const loadProductTags = useCallback(() => {
    return getProductTags()
      .then((response) => {
        const tags = response?.data?.data?.product_tags || [];

        if (response?.data?.success === true) {
          setProductTagsList(tags);
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }

        return tags;
      })
      .catch((error) => {
        handleApiError(error, t);
        return [];
      });
  }, [t]);

  // ---------- Load product tags ----------
  useEffect(() => {
    loadProductTags();
  }, [loadProductTags]);

  const normalizeEntityName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const getCreatedEntityId = (response, values, list, responseKey) => {
    const responseData =
      response?.data?.data || response?.response?.data?.data || {};
    const createdEntity = responseData?.[responseKey] || responseData;

    if (createdEntity?.id != null) {
      return createdEntity.id;
    }

    const englishName = normalizeEntityName(values?.name);
    const arabicName = String(values?.name_ar || "").trim();

    return list.find((item) => {
      const sameEnglishName =
        englishName && normalizeEntityName(item?.name) === englishName;
      const sameArabicName =
        arabicName && String(item?.name_ar || "").trim() === arabicName;

      return sameEnglishName || sameArabicName;
    })?.id;
  };

  const handleSingleEntityCreated = (loadList, fieldName, responseKey) => {
    return (response, values) => {
      loadList().then((list) => {
        const createdId = getCreatedEntityId(
          response,
          values,
          list,
          responseKey,
        );

        if (createdId != null) {
          productForm.setFieldsValue({ [fieldName]: createdId });
        }
      });
    };
  };

  const handleCategoryCreated = handleSingleEntityCreated(
    loadProductCategories,
    "category_id",
    "product_category",
  );

  const handleUnitCreated = handleSingleEntityCreated(
    loadProductUnits,
    "unit_id",
    "product_unit",
  );

  const handleOptionCreated = (response, values) => {
    loadProductTags().then((tags) => {
      const optionId = getCreatedEntityId(
        response,
        values,
        tags,
        "product_tag",
      );

      if (optionId != null) {
        const selectedOptionIds =
          productForm.getFieldValue("product_tag_ids") || [];

        productForm.setFieldsValue({
          product_tag_ids: Array.from(
            new Set([...selectedOptionIds, optionId]),
          ),
        });
      }
    });
  };

  // ---------- Load products list ----------
  useEffect(() => {
    let isEffectActive = true;

    setIsDataLoading(true);
    getProducts(searchParams)
      .then((response) => {
        if (response?.data?.success === false) {
          if (isEffectActive) handleApiError(response, t);
          return;
        }

        if (!isEffectActive) return;

        setProductsTableData(response?.data?.data?.products || []);
        setSelectedProductIds([]);
      })
      .catch((error) => {
        if (isEffectActive) handleApiError(error, t);
      })
      .finally(() => {
        if (isEffectActive) setIsDataLoading(false);
      });

    return () => {
      isEffectActive = false;
    };
  }, [reloadTableData, searchParams, t]);

  // ---------- Load taxes ----------
  useEffect(() => {
    setIsDataLoading(true);
    getAllTaxes()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTaxesData(response?.data?.data?.taxes || []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  // ---------- Add / Update submit ----------
  const handleAdd = () => {
    productForm
      .validateFields()
      .then((values) => {
        const formData = new window.FormData();

        applyAttachmentToPayload(formData, "image", image);

        // Flags
        is_QR_addon_active && (values.is_qr_product = isQRproduct ? 1 : 0);
        is_stock_management_active &&
          (values.is_stockable = isStockableProduct ? 1 : 0);

        values.is_taxable = isTaxable ? 1 : 0;
        isTaxable &&
          values.select_tax_ids?.forEach((tax_id) =>
            formData.append("tax_ids[]", tax_id),
          );

        is_ticketing_addon_active && (values.is_ticket = isTicket ? 1 : 0);

        // calories flags
        values.is_calories = isCalories ? 1 : 0;
        if (!isCalories) {
          values.calories_info = null;
        }

        if (values.product_tag_ids && values.product_tag_ids.length > 0) {
          values.product_tag_ids.forEach((tag_id) =>
            formData.append("tags[]", tag_id),
          );
        }

        Object.keys(values).forEach((key) => {
          if (["select_tax_ids", "product_tag_ids", "tags"].includes(key)) {
            return;
          }

          let value = values[key];

          if (key === "price") value = roundOff(value);
          if (key === "expiry_date" && value) {
            value = value.format("YYYY-MM-DD");
          }

          formData.append(key, value ?? "");
        });

        const isCreateMode = !modalData?.id;
        setLoadingBTN(true);

        if (isCreateMode) {
          createProduct(formData)
            .then((response) => handleAPIResponse(response, ADD_PRODUCT))
            .catch((error) => handleApiError(error, t))
            .finally(() => setLoadingBTN(false));
        } else {
          updateProduct(formData)
            .then((response) => handleAPIResponse(response, null))
            .catch((error) => handleApiError(error, t))
            .finally(() => setLoadingBTN(false));
        }
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    productForm.resetFields();
    setImage(false);
    setModalVisible(false);
    setModalData(null);
    setIsCalories(false);
  };

  const showAddProductModal = () => {
    setModalTitle(t("product_add"));
    setModalData(null);
    productForm.resetFields();
    setImage(false);
    setIsTaxable(false);
    setIsQRproduct(false);
    setIsStockableProduct(false);
    setIsTicket(false);
    setIsCalories(false);
    setModalVisible(true);
  };

  const handleAPIResponse = (response, type = null) => {
    let data = response?.data || response?.response?.data || {};
    if (data.success === true) {
      reloadProducts();
      NotificationWithIcon(
        "success",
        type === ADD_PRODUCT ? t("product_added") : t("product_updated"),
        type === ADD_PRODUCT
          ? t("product_added_successfully")
          : t("product_updated_successfully"),
      );
      productForm.resetFields();
      setImage(false);
      setModalVisible(false);
      setModalData(null);
      setIsCalories(false);
    } else if (data.success === false) {
      handleApiError(response, t);
    }
  };

  // ---------- Edit ----------
  const editProduct = (product_id) => {
    const product = productsTableData.find((prod) => prod.id === product_id);

    if (!product) return;

    const productName =
      appConfig.direction === "ltr" ? product.name_en : product.name;

    const formValues = {
      ...product,
      expiry_date: product.expiry_date
        ? moment(product.expiry_date, "YYYY-MM-DD")
        : null,
      select_tax_ids: product.taxes?.map(({ id }) => id) || [],
      product_tag_ids: product.tags?.map(({ id }) => id) || [],
    };

    setModalData(product);
    setModalTitle(`${t("product_edit")} : ${productName}`);
    setIsTaxable(!!product.is_taxable);
    setIsQRproduct(!!product.is_qr_product);
    setIsStockableProduct(!!product.is_stockable);
    setIsTicket(!!product.is_ticket);
    setIsCalories(!!product.is_calories);
    setImage(product.image || "");

    productForm.resetFields();
    productForm.setFieldsValue(formValues);

    setModalVisible(true);
  };

  // ---------- Delete ----------
  const handleDelete = (id) => {
    deleteProduct(id)
      .then((response) => {
        let data = response?.data || response?.response?.data || {};
        if (data.success === true) {
          reloadProducts();
          NotificationWithIcon(
            "success",
            t("product_deleted"),
            t("product_deleted_successfully"),
          );
        } else if (data.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  // ---------- Live image generation updates (websockets) ----------
  // Scoped to this page on purpose: away from here we track nothing, since
  // visiting the products page always refetches products (with images).
  useEffect(() => {
    if (!imageGenerationJob?.jobId) return undefined;

    const echo = getEcho();

    if (!echo || !window.Echo || typeof window.Echo.private !== "function") {
      // Websockets not configured: the job still runs server-side and fresh
      // images load with the next products fetch.
      NotificationWithIcon(
        "warning",
        t("something_went_wrong"),
        t("image_generation_live_updates_unavailable"),
      );
      setImageGenerationJob(null);
      return undefined;
    }

    setEchoAuthToken(localStorage.getItem("access_token"));

    const channelName = imageGenerationJob.channel;
    const channel = window.Echo.private(channelName);

    const applyImagesToTable = (imagesByProductId) => {
      setProductsTableData((prev) =>
        prev.map((product) =>
          imagesByProductId[product.id]
            ? { ...product, image: imagesByProductId[product.id] }
            : product,
        ),
      );
    };

    channel.listen(".product-image.generated", (event) => {
      if (event.job_id !== imageGenerationJob.jobId) return;

      if (event.product_id && event.image_url) {
        applyImagesToTable({ [event.product_id]: event.image_url });
      }

      setImageGenerationJob((prev) =>
        prev
          ? {
              ...prev,
              completed: event.completed ?? prev.completed,
              failed: event.failed ?? prev.failed,
              total: event.total ?? prev.total,
            }
          : prev,
      );
    });

    channel.listen(".product-images.batch-updated", (event) => {
      if (event.job_id !== imageGenerationJob.jobId) return;

      setImageGenerationJob((prev) =>
        prev
          ? {
              ...prev,
              completed: event.completed ?? prev.completed,
              failed: event.failed ?? prev.failed,
              skipped: Array.isArray(event.skipped)
                ? event.skipped.length
                : prev.skipped,
              total: event.total ?? prev.total,
            }
          : prev,
      );
    });

    channel.listen(".product-images.batch-completed", (event) => {
      if (event.job_id !== imageGenerationJob.jobId) return;

      const imagesByProductId = {};
      (Array.isArray(event.items) ? event.items : []).forEach((item) => {
        if (item?.product_id && item?.image_url) {
          imagesByProductId[item.product_id] = item.image_url;
        }
      });
      applyImagesToTable(imagesByProductId);

      if (event.status === ProductImageGenerationJobStatus.Failed) {
        showAppError(event.error_message || t("images_generation_failed"));
      } else if ((event.failed || 0) > 0) {
        NotificationWithIcon(
          "warning",
          t("images_generated"),
          t("images_generation_completed_with_errors", {
            completed: event.completed || 0,
            failed: event.failed || 0,
          }),
        );
      } else {
        NotificationWithIcon(
          "success",
          event.total === 1 ? t("image_generated") : t("images_generated"),
          event.total === 1
            ? t("image_generated_successfully")
            : t("images_generated_successfully", {
                count: event.completed || 0,
              }),
        );
      }

      setImageGenerationJob(null);
    });

    return () => {
      try {
        window.Echo?.leave?.(channelName);
      } catch {}
    };
    // Re-subscribing only makes sense when a new job starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageGenerationJob?.jobId]);

  const handleBulkImageGenerate = () => {
    const productIds = Array.from(new Set(selectedProductIds)).filter(Boolean);
    if (productIds.length === 0 || imageGenerationJob) return;

    bulkProductImageGenerate(productIds)
      .then((response) => {
        const status = response?.status;
        const data = response?.data || {};

        if (status === 200 && data.success === true) {
          // Every selected product already has an image.
          NotificationWithIcon(
            "info",
            t("images_generated"),
            data.message || t("products_already_have_images"),
          );
          setSelectedProductIds([]);
          return;
        }

        if (status === 202 && data.success === true && data.job_id) {
          setSelectedProductIds([]);
          setImageGenerationJob({
            jobId: data.job_id,
            channel: String(
              data.channel || `private-App.Models.User.${authUser.id}`,
            ).replace(/^private-/, ""),
            total: data.total || productIds.length,
            completed: data.completed || 0,
            failed: data.failed || 0,
            skipped: Array.isArray(data.skipped) ? data.skipped.length : 0,
          });
          NotificationWithIcon(
            "info",
            t("images_generation_started"),
            t("images_generation_started_desc", {
              count: data.total || productIds.length,
            }),
          );
          return;
        }

        handleApiError(response, t);
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "/products",
      breadcrumbName: t("products"),
      icon: <BarcodeOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => <p>{index + 1}</p>,
    },
    {
      title: t("image"),
      width: "85px",
      render: (product) => {
        return (
          product && (
            <img
              alt={product.name_en}
              src={
                product.image ? product.image : "/images/image-not-found.png"
              }
              style={{
                width: "50px",
                height: "auto",
                borderRadius: "1rem",
                border: "1px solid var(--color-border)",
                objectFit: "cover",
              }}
            />
          )
        );
      },
    },
    {
      title: t("product"),
      render: (product) =>
        appConfig.direction === "ltr" ? product.name_en : product.name,
    },
    {
      title: t("additional_info"),
      render: (product) => {
        return (
          <Space
            size="small"
            style={{
              display: "flex",
              flexWrap: "wrap",
              maxWidth: "260px",
            }}
          >
            {product.is_qr_product && is_QR_addon_active && (
              <Tag color="magenta">{t("is_qr_product")}</Tag>
            )}
            {product.is_taxable && <Tag color="volcano">{t("is_taxable")}</Tag>}
            {product.is_stockable && is_stock_management_active && (
              <Tag color="blue">{t("is_stockable")}</Tag>
            )}
            {product.is_taxable &&
              product.taxes?.map((tax, i) => (
                <Tag color="orange" key={i}>
                  {tax.title + " : " + tax.rate + "%"}
                </Tag>
              ))}
            {product.is_stockable &&
              is_stock_management_active &&
              product.low_stock >= product.stock && (
                <Tag color="red">{t("low_stock")}</Tag>
              )}
            {product.is_ticket && <Tag color="green">{t("is_ticket")}</Tag>}
            {authUser.is_calories &&
              product.is_calories &&
              product.calories_info && (
                <Tag color="geekblue">
                  {t("calories")}: {product.calories_info}
                </Tag>
              )}
            {product.tags &&
              product.tags.map((tag, i) => (
                <Tag color="cyan" key={i}>
                  {appConfig.direction === "ltr" ? tag.name : tag.name_ar}
                </Tag>
              ))}
          </Space>
        );
      },
    },
    {
      title: t("price"),
      dataIndex: "price",
      render: (price) => <CurrencyFormatter value={price} />,
    },
    {
      title: t("category"),
      render: (product) =>
        appConfig.direction === "ltr" ? product.category : product.category_ar,
    },
    {
      title: t("unit"),
      render: (product) =>
        appConfig.direction === "ltr" ? product.unit : product.unit_ar,
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      align: "center",
      width: "100px",
      render: (record) => (
        <Space>
          <Button
            type="link"
            title={t("edit")}
            onClick={() => editProduct(record.id)}
          >
            <EditOutlined />
          </Button>

          {userBusinessOwner && (
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ---------- Search ----------
  const handleSearch = (values) => {
    let search_params = false;
    if (values.filter_by_category_id) {
      search_params = { category_id: values.filter_by_category_id };
    }
    if (values.filter_by_unit_id) {
      search_params = { unit_id: values.filter_by_unit_id, ...search_params };
    }
    setSearchParams(search_params ? search_params : []);
  };

  const handleReset = () => {
    searchFormRef.resetFields();
    setSearchParams([]);
  };

  const filterByLabel = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const searchForm = (
    <Form
      name="search-form"
      layout="inline"
      onFinish={handleSearch}
      form={searchFormRef}
    >
      <Form.Item name="filter_by_category_id" label={t("category")}>
        <Select
          showSearch
          className="app-w-200px"
          options={categoriesList.map((category) => ({
            label:
              appConfig.direction === "ltr" ? category.name : category.name_ar,
            value: category.id,
          }))}
          filterOption={filterByLabel}
          allowClear
        />
      </Form.Item>
      <Form.Item name="filter_by_unit_id" label={t("unit")}>
        <Select
          showSearch
          className="app-w-200px"
          options={unitsList.map((unit) => ({
            label: appConfig.direction === "ltr" ? unit.name : unit.name_ar,
            value: unit.id,
          }))}
          filterOption={filterByLabel}
          allowClear
        />
      </Form.Item>
      <Form.Item>
        <Input
          type="submit"
          value={t("search")}
          className="app-search-submit"
        />
      </Form.Item>
      <Form.Item>
        <Input type="button" value={t("reset")} onClick={handleReset} />
      </Form.Item>
    </Form>
  );

  const handleArabicNameChange = (e) => {
    productForm.setFieldsValue({
      name: cleanArabicInput(e.target.value),
    });
  };

  const selectedProductsCount = selectedProductIds.length;
  const bulkGenerateButtonLabel =
    selectedProductsCount === 1
      ? t("generate_ai_image_for_one_product")
      : t("generate_ai_images_for_products", {
          count: selectedProductsCount,
        });

  const pageHeaderActions =
    selectedProductsCount > 0
      ? [
          <Button
            key="bulk-generate-product-images"
            type="primary"
            onClick={handleBulkImageGenerate}
            disabled={Boolean(imageGenerationJob)}
          >
            {bulkGenerateButtonLabel}
          </Button>,
        ]
      : [
          canManageProducts && (
            <Button
              key="open-add-product-modal"
              type="primary"
              onClick={showAddProductModal}
            >
              + {t("product_add")}
            </Button>
          ),
          authUser.type !== UserType.Employee && (
            <DownloadImportTemplate type="product" key="import-template" />
          ),
          authUser.type !== UserType.Employee && (
            <ImportItems
              type="product"
              callbackLoading={setReloadTableData}
              key="import-items"
            />
          ),
        ].filter(Boolean);

  const productRowSelection = canManageProducts
    ? {
        selectedRowKeys: selectedProductIds,
        onChange: (selectedRowKeys) => setSelectedProductIds(selectedRowKeys),
        getCheckboxProps: (record) => ({
          name: appConfig.direction === "ltr" ? record.name_en : record.name,
        }),
      }
    : undefined;

  return (
    <>
      {imageGenerationJob && (
        <Alert
          type="info"
          showIcon
          className="app-mb-16"
          message={
            imageGenerationJob.total > 1
              ? t("generating_images_with_ai", {
                  count: imageGenerationJob.total,
                })
              : t("generating_image_with_ai")
          }
          description={
            <Progress
              percent={Math.round(
                (((imageGenerationJob.completed || 0) +
                  (imageGenerationJob.failed || 0) +
                  (imageGenerationJob.skipped || 0)) /
                  Math.max(imageGenerationJob.total || 1, 1)) *
                  100,
              )}
              size="small"
              status="active"
            />
          }
        />
      )}

      <Spin spinning={isDataLoading}>
        <Card className="app-mb-20">
          <CustomBreadcrumb menus={breadcrumbRoutes} />
          <PageHeader
            className="app-p-0"
            title={t("products")}
            ghost={false}
            footer={searchForm}
            extra={pageHeaderActions}
          />
        </Card>

        <Card>
          <Table
            columns={
              canManageProducts
                ? [...detailsPartColumns, ...actionPartColumn]
                : [...detailsPartColumns]
            }
            dataSource={productsTableData}
            rowKey="id"
            rowSelection={productRowSelection}
            pagination={false}
          />
        </Card>

        <Modal
          open={modalVisible}
          title={modalTitle}
          onOk={handleAdd}
          onCancel={handleCancel}
          width={900}
          footer={[
            <Button
              type="default"
              key="back"
              loading={loadingBTN}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={loadingBTN}
              onClick={handleAdd}
            >
              {modalData?.id ? t("update") : t("product_add")}
            </Button>,
          ]}
        >
          <Form
            name="add-product-form"
            layout="vertical"
            onFinish={handleAdd}
            className="app-form-full"
            form={productForm}
          >
            <div className="ant-col ant-form-item-label">
              <label title={t("image")}>{t("image")}</label>
            </div>

            <div
              style={{
                display: "flex",
                columnGap: "15px",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <ImageUpload
                name="product_image"
                callbackSetImageFile={setImage}
                image={image}
              />
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label={t("name_ar")}
                  rules={arabicNameRules(t, true)}
                  hasFeedback
                >
                  <Input
                    dir="rtl"
                    placeholder={t("arabic_only_validation")}
                    onChange={handleArabicNameChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name_en"
                  label={t("name_en")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_product_name_required"),
                      whitespace: true,
                    },
                  ]}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="price"
                  label={t("price")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_product_price_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    columnGap: "8px",
                  }}
                >
                  <Form.Item
                    name="category_id"
                    label={t("category")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_product_category_required"),
                      },
                    ]}
                    hasFeedback
                    style={{ width: "100%", marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      options={categoriesList.map((category) => ({
                        label:
                          appConfig.direction === "ltr"
                            ? category.name
                            : category.name_ar,
                        value: category.id,
                      }))}
                      filterOption={filterByLabel}
                    />
                  </Form.Item>

                  {userBusinessOwner && (
                    <div style={{ paddingTop: "30px" }}>
                      <AddCategory onSuccess={handleCategoryCreated} />
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    columnGap: "8px",
                  }}
                >
                  <Form.Item
                    name="unit_id"
                    label={t("unit")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_product_unit_required"),
                      },
                    ]}
                    hasFeedback
                    style={{ width: "100%", marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      options={unitsList.map((unit) => ({
                        label:
                          appConfig.direction === "ltr"
                            ? unit.name
                            : unit.name_ar,
                        value: unit.id,
                      }))}
                      filterOption={filterByLabel}
                    />
                  </Form.Item>

                  {userBusinessOwner && (
                    <div style={{ paddingTop: "30px" }}>
                      <AddUnit onSuccess={handleUnitCreated} />
                    </div>
                  )}
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    columnGap: "8px",
                  }}
                >
                  <Form.Item
                    name="product_tag_ids"
                    label={t("options")}
                    hasFeedback
                    style={{ width: "100%", marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      mode="multiple"
                      allowClear
                      options={productTagsList.map((tag) => ({
                        label:
                          appConfig.direction === "ltr"
                            ? tag.name
                            : tag.name_ar,
                        value: tag.id,
                      }))}
                      filterOption={filterByLabel}
                    />
                  </Form.Item>

                  {userBusinessOwner && (
                    <div style={{ paddingTop: "30px" }}>
                      <AddOption onSuccess={handleOptionCreated} />
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <h3>{t("configuration")}</h3>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="is_taxable" label={t("is_taxable")}>
                  <Switch
                    style={{
                      backgroundColor: isTaxable
                        ? "var(--color-success-hover)"
                        : "var(--color-danger)",
                    }}
                    checked={
                      authUser.is_vat_exempt || authUser.vat === null
                        ? false
                        : isTaxable
                    }
                    checkedChildren={t("yes")}
                    unCheckedChildren={t("no")}
                    onChange={(value) => {
                      setIsTaxable(value);
                      productForm.setFieldsValue({
                        select_tax_ids: undefined,
                      });
                    }}
                    disabled={authUser.is_vat_exempt || authUser.vat === null}
                  />
                </Form.Item>
              </Col>

              {is_stock_management_active && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item name="is_stockable" label={t("is_stockable")}>
                    <Switch
                      style={{
                        backgroundColor: isStockableProduct
                          ? "var(--color-success-hover)"
                          : "var(--color-danger)",
                      }}
                      checked={isStockableProduct}
                      checkedChildren={t("yes")}
                      unCheckedChildren={t("no")}
                      onChange={(value) => {
                        setIsStockableProduct(value);
                        productForm.setFieldsValue({
                          low_stock: undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              )}

              {is_QR_addon_active && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item name="is_qr_product" label={t("is_qr_product")}>
                    <Switch
                      style={{
                        backgroundColor: isQRproduct
                          ? "var(--color-success-hover)"
                          : "var(--color-danger)",
                      }}
                      checked={isQRproduct}
                      checkedChildren={t("yes")}
                      unCheckedChildren={t("no")}
                      onChange={(value) => setIsQRproduct(value)}
                    />
                  </Form.Item>
                </Col>
              )}

              {is_ticketing_addon_active && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item name="is_ticket" label={t("is_ticket")}>
                    <Switch
                      style={{
                        backgroundColor: isTicket
                          ? "var(--color-success-hover)"
                          : "var(--color-danger)",
                      }}
                      checked={isTicket}
                      checkedChildren={t("yes")}
                      unCheckedChildren={t("no")}
                      onChange={(value) => {
                        setIsTicket(value);
                        productForm.setFieldsValue({
                          expiry_date: undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              {authUser.is_calories && (
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item name="is_calories" label={t("is_calories")}>
                    <Switch
                      style={{
                        backgroundColor: isCalories
                          ? "var(--color-success-hover)"
                          : "var(--color-danger)",
                      }}
                      checked={isCalories}
                      checkedChildren={t("yes")}
                      unCheckedChildren={t("no")}
                      onChange={(value) => {
                        setIsCalories(value);
                        if (!value) {
                          productForm.setFieldsValue({
                            calories_info: undefined,
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              {isTaxable && (
                <Col span={24}>
                  <Form.Item
                    name="select_tax_ids"
                    label={t("select_taxes")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_value_required"),
                      },
                    ]}
                    hasFeedback
                  >
                    <Select
                      showSearch
                      mode="multiple"
                      allowClear
                      onChange={() => taxesSelect.current?.blur?.()}
                      ref={taxesSelect}
                      options={taxesData.map((tax) => ({
                        label: tax.title,
                        value: tax.id,
                      }))}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              )}

              {is_stock_management_active && isStockableProduct && (
                <Col span={24}>
                  <Form.Item
                    name="low_stock"
                    label={t("low_stock")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_low_stock_required"),
                      },
                    ]}
                    hasFeedback
                  >
                    <Input type="number" />
                  </Form.Item>
                </Col>
              )}

              {is_ticketing_addon_active &&
                is_stock_management_active &&
                isStockableProduct &&
                isTicket && (
                  <Col span={24}>
                    <Form.Item
                      name="expiry_date"
                      label={t("expiry_date")}
                      rules={[
                        {
                          required: true,
                          message: t("validate_expiry_date_required"),
                        },
                      ]}
                      hasFeedback
                    >
                      <DatePicker
                        disabledDate={(current) =>
                          current && current < moment().add(-1, "day")
                        }
                        format="YYYY-MM-DD"
                        allowClear
                        className="app-w-full"
                      />
                    </Form.Item>
                  </Col>
                )}

              {isCalories && (
                <Col span={24}>
                  <Form.Item
                    name="calories_info"
                    label={t("calories_info")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_calories_info_required"),
                      },
                    ]}
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item name="id" className="app-hidden">
              <input type="hidden" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </>
  );
};

export default Products;
